import { DialogCloseEvent, DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #needsCartRefreshOnOpen = false;
  #cartRefreshPromise = null;
  #drawerHistoryActive = false;
  #ignoreNextPopState = false;
  #closingFromDrawerHistory = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    window.addEventListener('popstate', this.#handlePopState);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  #handlePopState = () => {
    if (this.#ignoreNextPopState) {
      this.#ignoreNextPopState = false;
      return;
    }

    if (!this.#drawerHistoryActive) return;

    this.#drawerHistoryActive = false;

    if (!this.refs.dialog?.open) return;

    this.#closingFromDrawerHistory = true;
    this.closeDialog().finally(() => {
      this.#closingFromDrawerHistory = false;
    });
  };

  #handleDialogClose = () => {
    if (this.#closingFromDrawerHistory) return;
    this.#removeDrawerHistoryEntry();
  };

  #handlePageShow = (event) => {
    const navigationEntry = performance.getEntriesByType?.('navigation')?.[0];
    const restoredFromHistory = event.persisted || navigationEntry?.type === 'back_forward';

    if (!restoredFromHistory) return;

    this.#needsCartRefreshOnOpen = true;

    if (this.refs.dialog?.open) {
      this.#refreshCartItemsFromSection().catch((error) => console.error(error));
    }
  };

  open() {
    const wasOpen = this.refs.dialog?.open;

    this.showDialog();
    if (!wasOpen && this.refs.dialog?.open) {
      this.#pushDrawerHistoryEntry();
    }
    this.#refreshCartItemsAfterHistoryRestore().catch((error) => console.error(error));

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #pushDrawerHistoryEntry() {
    if (this.#drawerHistoryActive) return;

    try {
      const state = history.state && typeof history.state === 'object' ? { ...history.state } : {};

      history.pushState({ ...state, sityaCartDrawer: true }, '', window.location.href);
      this.#drawerHistoryActive = true;
    } catch (error) {
      console.error(error);
    }
  }

  #removeDrawerHistoryEntry() {
    if (!this.#drawerHistoryActive) return;

    this.#drawerHistoryActive = false;
    this.#ignoreNextPopState = true;

    try {
      history.back();
      window.setTimeout(() => {
        this.#ignoreNextPopState = false;
      }, 500);
    } catch (error) {
      this.#ignoreNextPopState = false;
      console.error(error);
    }
  }

  async #refreshCartItemsAfterHistoryRestore() {
    if (!this.#needsCartRefreshOnOpen) return;

    this.#needsCartRefreshOnOpen = false;
    await this.#refreshCartItemsFromSection();
  }

  async #refreshCartItemsFromSection() {
    if (this.#cartRefreshPromise) return this.#cartRefreshPromise;

    this.#cartRefreshPromise = this.#refreshCartItems();

    try {
      await this.#cartRefreshPromise;
    } finally {
      this.#cartRefreshPromise = null;
    }
  }

  async #refreshCartItems() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId = cartItemsComponent?.dataset.sectionId;

    if (!cartItemsComponent || !sectionId) return;

    const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false);
    const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newCartItemsComponent = newSectionHTML.querySelector('cart-drawer-component cart-items-component');

    if (!newCartItemsComponent) return;

    morph(cartItemsComponent, newCartItemsComponent);
    this.ensureModalDialog?.();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
